// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import { X } from 'lucide-react';

import { AuthContext } from '../context/AuthContext';
import { useTranslation } from '../i18n/i18n';

const CANCEL_BUTTON_CLASS =
  'h-12 min-w-[10.625rem] rounded-lg flex items-center justify-center border border-[#E1E1E1] dark:border-[#3C3E53] bg-[#FAFAFA] hover:bg-[#F5F5F5] active:bg-[#E1E1E1] dark:bg-[#0C121D] dark:hover:bg-[#1A202E] dark:active:bg-[#0C121D] py-2 px-4 text-lg leading-[120%] font-medium dark:text-[#CAD5E2] box-border transition-all duration-300';

const PRIMARY_BUTTON_CLASS =
  'h-12 min-w-[10.625rem] rounded-lg flex items-center justify-center text-lg leading-[120%] font-medium text-[#FFFFFF] dark:text-[#FAFAFA] bg-[#2563EB] hover:bg-[#1E4FBB] active:bg-[#113C91] dark:bg-[#2773B7] dark:hover:bg-[#1D649C] dark:active:bg-[#2773B7] py-2 px-4 box-border transition-all duration-300';

export default function StampTimeoutModal({ handleYes, handleNo, selection, suppliers }) {
  const { currentUser } = React.useContext(AuthContext);
  const { t } = useTranslation(currentUser?.info?.languageCode);

  const supplier = selection?.supplier_id ? suppliers?.[selection.supplier_id] : null;

  return (
    <div className='fixed inset-0 z-[1000] bg-black/70 backdrop-blur-sm flex justify-center items-center animate-fadein'>
      <div className='rounded-3xl dark:border dark:border-[#3C3E53] bg-[#FFFFFFF2] dark:bg-[#2E3440] shadow-[58px_41px_42px_0_rgba(0,0,0,0.07),_6px_5px_17px_0_rgba(0,0,0,0.13)] dark:shadow-[0_10px_15px_-3px_rgba(0,0,0,0.10)] p-6 flex flex-col gap-2 max-w-[25rem] w-full relative animate-popup'>
        <X
          className='size-5 text-[#7E7E7E] hover:text-[#4B4B4B] dark:text-[#90A1B9] dark:hover:text-[#CAD5E2] absolute top-5 right-5 cursor-pointer transition-all duration-300'
          onClick={() => handleNo?.()}
        />
        <p className='text-xl font-semibold text-[#0D0D0D] dark:text-[#FAFAFA] text-center px-5'>
          {t('stamp_timeout_title')}
        </p>
        <p className='text-base font-medium text-[#4B4B4B] dark:text-[#CAD5E2] text-center'>
          {t('stamp_timeout_message')}
        </p>

        {selection?.supplier_id && (
          <div className='mt-2 flex flex-col items-center gap-1 text-center'>
            <p className='text-base font-semibold text-[#0D0D0D] dark:text-[#FAFAFA]'>
              {supplier?.supplier_code ?? ''}
            </p>
            <p className='text-base font-medium text-[#4B4B4B] dark:text-[#CAD5E2]'>
              {supplier?.display_name ?? selection.supplier_id}
            </p>
            <p className='text-sm font-medium text-[#8A8C95] dark:text-[#CAD5E2]'>
              {t('stamp_timeout_field').replace('{field}', selection.field)}
            </p>
          </div>
        )}

        <div className='flex items-center justify-end gap-3 mt-6'>
          <button type='button' className={CANCEL_BUTTON_CLASS} onClick={() => handleNo?.()}>
            {t('stamp_timeout_no')}
          </button>
          <button type='button' className={PRIMARY_BUTTON_CLASS} onClick={() => handleYes?.()}>
            {t('stamp_timeout_yes')}
          </button>
        </div>
      </div>
    </div>
  );
}
